import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, Sparkles, Camera, Instagram, Phone } from "lucide-react";

const MAKEUP_URL = "https://www.dejavumakeup.com";
const INSTAGRAM_URL = "https://www.instagram.com/dejavumakeupbyvinni/";
const PHONE = "8095500005";

const MAKEUP_IMG = "/home-makeup.webp";
const PHOTO_IMG = "/home-photography.webp";

export default function ComingSoonPage() {
  return (
    <div className="min-h-screen bg-[#2A2421] text-[#FAF9F6] flex flex-col" data-testid="coming-soon-page">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 sm:px-10 py-6">
        <div className="leading-none">
          <div className="font-serif text-2xl tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Dejavu</div>
          <div className="text-[9px] uppercase tracking-[0.35em] text-[#C88270] mt-1">Weddings</div>
        </div>
        <Link
          to="/login"
          data-testid="admin-login-shortcut"
          className="inline-flex items-center gap-2 border border-[#4A423D] px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[#E8E2DA] hover:border-[#C88270] hover:text-[#C88270] transition-colors"
        >
          <Lock size={13} /> <span className="hidden sm:inline">Admin / Team</span> Login
        </Link>
      </header>

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-10 text-center">
        <div className="inline-flex items-center gap-2 border border-[#4A423D] rounded-full px-4 py-1.5 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#C88270] animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9C1B8]">Coming Soon</span>
        </div>

        <h1
          className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Something beautiful is on its way
        </h1>
        <p className="text-sm sm:text-base text-[#B7AFA6] mt-5 max-w-lg">
          Two houses, one vision. Explore our makeup artistry now — our photography studio is being crafted with care.
        </p>

        {/* Service cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14 w-full max-w-4xl">
          {/* Makeup by Vinni — live */}
          <a
            href={MAKEUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="service-makeup-by-vinni"
            className="group relative overflow-hidden rounded-sm border border-[#4A423D] text-left aspect-[4/5] sm:aspect-[16/13] hover:border-[#C88270] transition-colors"
          >
            <img
              src={MAKEUP_IMG}
              alt="Makeup by Vinni"
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1c1815]/95 via-[#1c1815]/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
              <div className="inline-flex items-center gap-1.5 self-start mb-3 bg-[#C88270] text-[#2A2421] text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full font-medium">
                <Sparkles size={11} /> Live now
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#E4C9C0] mb-1">Makeup by Vinni</div>
              <h2 className="font-serif text-3xl sm:text-4xl leading-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Just your face, Elevated
              </h2>
              <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-white mt-4 group-hover:text-[#C88270] transition-colors">
                Visit dejavumakeup.com <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </span>
            </div>
          </a>

          {/* Dejavu Photography — coming soon */}
          <div
            data-testid="service-dejavu-photography"
            className="group relative overflow-hidden rounded-sm border border-[#4A423D] text-left aspect-[4/5] sm:aspect-[16/13] cursor-default"
          >
            <img
              src={PHOTO_IMG}
              alt="Dejavu Photography"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover object-center grayscale opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1c1815]/95 via-[#1c1815]/55 to-[#1c1815]/25" />
            <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
              <div className="inline-flex items-center gap-1.5 self-start mb-3 border border-[#6b6259] text-[#C9C1B8] text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full">
                <Camera size={11} /> Coming Soon
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#B7AFA6] mb-1">Dejavu Photography</div>
              <h2 className="font-serif text-3xl sm:text-4xl leading-none text-[#E8E2DA]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Frames that stay forever
              </h2>
              <span className="text-xs uppercase tracking-[0.18em] text-[#8f867d] mt-4">Launching shortly</span>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mt-12">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-link"
            className="inline-flex items-center gap-2 border border-[#4A423D] px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] text-[#E8E2DA] hover:border-[#C88270] hover:text-[#C88270] transition-colors"
          >
            <Instagram size={15} /> @dejavumakeupbyvinni
          </a>
          <a
            href={`tel:${PHONE}`}
            data-testid="phone-link"
            className="inline-flex items-center gap-2 border border-[#4A423D] px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] text-[#E8E2DA] hover:border-[#C88270] hover:text-[#C88270] transition-colors"
          >
            <Phone size={15} /> {PHONE}
          </a>
        </div>
      </main>

      <footer className="px-6 sm:px-10 py-6 text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#6b6259]">
          © {new Date().getFullYear()} Dejavu · Makeup &amp; Photography
        </p>
      </footer>
    </div>
  );
}
